import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { UtilService } from '../../../../shared/services/util.service';
import { LocalizacaoEstoqueService } from '../../../../shared/services/apis/produto/localizacao-estoque.service';
import { LocalizacaoEstoqueStruct } from '../../../../shared/services/responses/produto/get-localizacao-estoque.response';

@Component({
  selector: 'app-localizacao-estoque',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    MatChipsModule,
    EmptyListComponent,
    RouterModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './localizacao-estoque.component.html',
  styleUrl: './localizacao-estoque.component.css',
})
export class LocalizacaoEstoqueComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Suprimentos;
  public model: FormGroup;
  public _paginaAtual: number = 1;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;
  public _dataSource = new DataSourceOrdenacaoInsensivel<LocalizacaoEstoqueStruct>();
  public _isLoading: boolean = false;
  public displayedColumns: string[] = ['select', 'codigo', 'nome', 'isPadrao', 'isAtivo', 'actions'];
  public selection = new SelectionModel<LocalizacaoEstoqueStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private localizacaoEstoqueService: LocalizacaoEstoqueService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({ pesquisa: [''] });
    this.loadFilters();
    this.carregarDados();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/suprimentos/localizacao-estoque/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public carregarDados(reset: boolean = false): void {
    this._isLoading = true;
    const pesquisa = this.model.get('pesquisa')?.value || '';
    this.localizacaoEstoqueService.getAll(pesquisa, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription || 'Erro ao carregar localizações', AlertType.error);
          return;
        }
        this._totalRegistros = response.totalRegistros;
        if (reset) {
          this._dataSource.data = response.listaLocalizacaoEstoque;
        } else {
          this._dataSource.data = [...this._dataSource.data, ...response.listaLocalizacaoEstoque];
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err.message || 'Erro ao carregar localizações', AlertType.error);
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarDados();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 1;
    this.selection.clear();
    this._dataSource.data = [];
    this.carregarDados(true);
  }

  public isAllSelected() {
    return (
      this.selection.selected.length === this._dataSource.data.length && this._dataSource.data.length > 0
    );
  }

  public toggleAllRows() {
    this.isAllSelected() ? this.selection.clear() : this.selection.select(...this._dataSource.data);
  }

  public abrirModalExcluir(item?: LocalizacaoEstoqueStruct): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];
    if (itemsToDelete.length === 0) return;

    const description = isBulk
      ? 'Confirma a exclusão das localizações selecionadas?'
      : 'Confirma a exclusão da localização selecionada?';

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar exclusão',
        description,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;

      this._isLoading = true;
      if (isBulk) {
        this.localizacaoEstoqueService.deleteBulk(itemsToDelete.map(i => i.idLocalizacaoEstoque)).subscribe({
          next: r => {
            this._isLoading = false;
            if (r.isError) {
              this.alertService.show('Atenção', r.errorDescription, AlertType.warning);
            } else if (r.totalBloqueadas > 0) {
              this.alertService.show(
                'Atenção',
                r.errorDescription || 'Algumas localizações não foram excluídas pois estão vinculadas a produtos.',
                AlertType.warning
              );
            } else {
              this.alertService.show('Sucesso', 'Localizações excluídas com sucesso.', AlertType.success);
            }
            this.selection.clear();
            this.clickPesquisar();
          },
          error: err => {
            this._isLoading = false;
            this.alertService.show('Erro', err.message, AlertType.error);
          },
        });
      } else {
        this.localizacaoEstoqueService.delete(item!.idLocalizacaoEstoque).subscribe({
          next: r => {
            this._isLoading = false;
            if (r.isError) {
              this.alertService.show('Erro', r.errorDescription, AlertType.error);
            } else {
              this.alertService.show('Sucesso', 'Localização excluída com sucesso.', AlertType.success);
              this.clickPesquisar();
            }
          },
          error: err => {
            this._isLoading = false;
            this.alertService.show('Erro', err.message, AlertType.error);
          },
        });
      }
    });
  }

  public abrirModalInativar(): void {
    const items = this.selection.selected;
    if (items.length === 0) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar inativação',
        description: 'Confirma a inativação das localizações selecionadas?',
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;

      this._isLoading = true;
  this.localizacaoEstoqueService.inactivateBulk(items.map(i => i.idLocalizacaoEstoque)).subscribe({
        next: r => {
          this._isLoading = false;
          if (r.isError) {
            this.alertService.show('Atenção', r.errorDescription, AlertType.warning);
          } else if (r.totalBloqueadas > 0) {
            this.alertService.show(
              'Atenção',
              r.errorDescription || 'Algumas localizações não foram inativadas pois estão vinculadas a produtos.',
              AlertType.warning
            );
          } else {
            this.alertService.show('Sucesso', 'Localizações inativadas com sucesso.', AlertType.success);
          }
          this.selection.clear();
          this.clickPesquisar();
        },
        error: err => {
          this._isLoading = false;
          this.alertService.show('Erro', err.message, AlertType.error);
        },
      });
    });
  }

  public alterarStatus(item: LocalizacaoEstoqueStruct, isAtivo: boolean): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: isAtivo ? 'Confirmar ativação' : 'Confirmar inativação',
        description: isAtivo
          ? `Confirma a ativação da localização "${item.nome}"?`
          : `Confirma a inativação da localização "${item.nome}"?`,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;

      this._isLoading = true;
      this.localizacaoEstoqueService.setAtivo(item.idLocalizacaoEstoque, isAtivo).subscribe({
        next: r => {
          this._isLoading = false;
          if (r.isError) {
            this.alertService.show('Atenção', r.errorDescription, AlertType.warning);
          } else {
            this.alertService.show(
              'Sucesso',
              isAtivo ? 'Localização ativada com sucesso.' : 'Localização inativada com sucesso.',
              AlertType.success
            );
            this.clickPesquisar();
          }
        },
        error: err => {
          this._isLoading = false;
          this.alertService.show('Erro', err.message, AlertType.error);
        },
      });
    });
  }

  public tornarPadrao(item: LocalizacaoEstoqueStruct): void {
    if (item.isPadrao) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Definir como padrão',
        description: `Confirma definir "${item.nome}" como localização padrão?`,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;

      this._isLoading = true;
  this.localizacaoEstoqueService.setDefault(item.idLocalizacaoEstoque).subscribe({
        next: r => {
          this._isLoading = false;
          if (r.isError) {
            this.alertService.show('Erro', r.errorDescription, AlertType.error);
          } else {
            this.alertService.show('Sucesso', 'Localização definida como padrão.', AlertType.success);
            this.clickPesquisar();
          }
        },
        error: err => {
          this._isLoading = false;
          this.alertService.show('Erro', err.message, AlertType.error);
        },
      });
    });
  }
}
